import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { fmtDate, toInputDate } from '../utils';
import { Badge, Modal, Pagination, ConfirmDialog } from '../components/ui';
import { useAuth } from '../context/AuthContext';

export function ContactForm({ contact, onSave, onClose }) {
  const [form, setForm] = useState({ first_name:'',last_name:'',email:'',phone:'',title:'',department:'',account_id:'',relationship_strength:'None',existing_customer:'No',status:'active',notes:'', ...contact });
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => { api.get('/accounts', { params: { limit:200 } }).then(r => setAccounts(r.data.data)); }, []);

  const handleSave = async () => {
    if (!form.first_name || !form.last_name) return setError('First and last name required');
    setSaving(true);
    try {
      const r = contact?.id ? await api.put(`/contacts/${contact.id}`, form) : await api.post('/contacts', form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={contact?.id ? 'Edit Contact' : 'New Contact'} onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-grid">
          <div className="form-group"><label className="form-label">First Name *</label><input className="form-input" value={form.first_name} onChange={set('first_name')} /></div>
          <div className="form-group"><label className="form-label">Last Name *</label><input className="form-input" value={form.last_name} onChange={set('last_name')} /></div>
        </div>
        <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={set('email')} /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={set('phone')} /></div>
          <div className="form-group"><label className="form-label">Title</label><input className="form-input" value={form.title} onChange={set('title')} /></div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Relationship Strength</label>
            <select className="form-select" value={form.relationship_strength} onChange={set('relationship_strength')}>
              {['Strong','Medium','Weak','None'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Existing Customer</label>
            <select className="form-select" value={form.existing_customer} onChange={set('existing_customer')}>
              <option value="Yes">Yes</option><option value="No">No</option>
            </select>
          </div>
        </div>
        <div className="form-group"><label className="form-label">Account</label>
          <select className="form-select" value={form.account_id} onChange={set('account_id')}>
            <option value="">No account</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={set('notes')} rows={3} /></div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Contact'}</button>
      </div>
    </Modal>
  );
}

export default function Contacts() {
  const [contacts, setContacts] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(1);
  const [search, setSearch] = useState(''); const [showForm, setShowForm] = useState(false); const [editContact, setEditContact] = useState(null);
  const [deactivateId, setDeactivateId] = useState(null); const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/contacts', { params: { search, page, limit:25 } }); setContacts(r.data.data); setTotal(r.data.total); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="page-header"><span className="page-title">Contacts</span>
        {can('edit') && <button className="btn btn-primary" onClick={() => { setEditContact(null); setShowForm(true); }}>+ New Contact</button>}
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body" style={{ paddingBottom:12 }}>
            <div className="toolbar">
              <input className="search-input" placeholder="Search contacts..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
              <span className="text-muted text-sm" style={{ marginLeft:'auto' }}>{total} contacts</span>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Name</th><th>Title</th><th>Account</th><th>Email</th><th>Relationship</th><th>Customer</th><th></th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={7} style={{ textAlign:'center',padding:40,color:'var(--gray-400)' }}>Loading...</td></tr>}
                {!loading && contacts.length === 0 && <tr><td colSpan={7}><div className="empty-state"><div className="empty-state-icon">👤</div><div className="empty-state-text">No contacts found</div></div></td></tr>}
                {contacts.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)}>
                    <td><span className="td-link">{c.first_name} {c.last_name}</span></td>
                    <td>{c.title||'—'}</td>
                    <td>{c.account_name ? <span className="td-link" onClick={e => { e.stopPropagation(); navigate(`/accounts/${c.account_id}`); }}>{c.account_name}</span> : '—'}</td>
                    <td>{c.email||'—'}</td>
                    <td><Badge status={c.relationship_strength} /></td>
                    <td>{c.existing_customer === 'Yes' ? <span style={{ color:'var(--success)',fontWeight:600,fontSize:12 }}>✓ Yes</span> : <span style={{ color:'var(--gray-400)',fontSize:12 }}>No</span>}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {can('edit') && <div style={{ display:'flex',gap:4 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditContact(c); setShowForm(true); }}>✏️</button>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Deactivate" onClick={() => setDeactivateId(c.id)}>📦</button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} limit={25} total={total} onPage={setPage} />
        </div>
      </div>
      {showForm && <ContactForm contact={editContact} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
      {deactivateId && <ConfirmDialog message="Mark contact as inactive? Record is preserved." confirmLabel="Deactivate" onConfirm={async () => { await api.delete(`/contacts/${deactivateId}`); setDeactivateId(null); load(); }} onCancel={() => setDeactivateId(null)} />}
    </>
  );
}
